import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function CTA({ label, url }) {
  if (!label) return null;
  const cls = "inline-flex items-center bg-heraldic text-white px-6 py-3 rounded-sm text-sm font-medium hover:opacity-90 transition-opacity";
  return (url || "").startsWith("http")
    ? <a href={url} className={cls}>{label}</a>
    : <Link to={url || "/apply"} className={cls}>{label}</Link>;
}

export default function LandingPage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [page, setPage] = useState(undefined);

  useEffect(() => {
    api.get(`/pages/${slug}`).then((r) => {
      setPage(r.data);
      if (r.data.seo?.title_en) document.title = r.data.seo.title_en;
    }).catch(() => setPage(null));
  }, [slug]);

  if (page === undefined) return <div className="min-h-screen flex items-center justify-center text-ink/40">Loading…</div>;
  if (page === null) return <div className="min-h-screen flex flex-col items-center justify-center gap-3"><h1 className="font-serif text-3xl">Page not found</h1><Link to="/" className="text-heraldic">Return home</Link></div>;

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="landing-page">
      {(page.sections || []).map((s, i) => {
        const d = s.data || {};
        if (s.type === "hero") return (
          <section key={i} className="relative px-6 py-24 sm:py-32 bg-ink text-paper overflow-hidden" style={d.image ? { backgroundImage: `url(${d.image.startsWith("/api") ? BACKEND + d.image : d.image})`, backgroundSize: "cover", backgroundPosition: "center" } : {}} data-testid="ls-hero">
            {d.image && <div className="absolute inset-0 bg-black/55" />}
            <div className="relative max-w-4xl mx-auto text-center">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">{d.headline}</h1>
              {d.subhead && <p className="mt-5 text-base md:text-lg text-paper/70 max-w-2xl mx-auto">{d.subhead}</p>}
              <div className="mt-8"><CTA label={d.cta_label} url={d.cta_url} /></div>
            </div>
          </section>
        );
        if (s.type === "richtext") return (
          <section key={i} className="px-6 py-16 max-w-3xl mx-auto" data-testid="ls-richtext">
            {d.heading && <h2 className="font-serif text-3xl mb-4">{d.heading}</h2>}
            <p className="text-base text-ink/70 whitespace-pre-line leading-relaxed">{d.body}</p>
          </section>
        );
        if (s.type === "features") return (
          <section key={i} className="px-6 py-16 max-w-5xl mx-auto" data-testid="ls-features">
            {d.heading && <h2 className="font-serif text-3xl mb-8 text-center">{d.heading}</h2>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(d.items || []).map((it, j) => <div key={j} className="border-l-2 border-heraldic pl-4"><div className="font-medium">{it.title}</div><div className="text-sm text-ink/60 mt-1">{it.body}</div></div>)}
            </div>
          </section>
        );
        if (s.type === "image") return (
          <section key={i} className="px-6 py-10 max-w-4xl mx-auto text-center" data-testid="ls-image">
            <img src={d.url?.startsWith("/api") ? BACKEND + d.url : d.url} alt={d.caption || ""} className="w-full rounded-md" />
            {d.caption && <div className="text-xs text-ink/50 mt-2">{d.caption}</div>}
          </section>
        );
        if (s.type === "cta") return (
          <section key={i} className="px-6 py-20 bg-ink text-paper text-center" data-testid="ls-cta">
            <h2 className="font-serif text-3xl mb-6 max-w-2xl mx-auto">{d.headline}</h2>
            <CTA label={d.cta_label} url={d.cta_url} />
          </section>
        );
        return null;
      })}
      {(!page.sections || page.sections.length === 0) && (
        <div className="min-h-screen flex items-center justify-center text-ink/40">This page has no content yet.</div>
      )}
    </div>
  );
}
